import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Search, ArrowRight, Inbox, Users, Building2, Briefcase, FileSignature,
  CheckSquare, Activity, BarChart3, Settings, ClipboardList,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { searchApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useModKey } from "@/hooks/useModKey";
import { CONTRACT_BUILDER_ENABLED } from "@/lib/flags";

interface PaletteItem {
  id: string;
  title: string;
  hint?: string;
  icon?: typeof Inbox;
  to: string;
  group: string;
}

const STATIC_NAV: PaletteItem[] = [
  { id: "nav:inbox", title: "Inbox", icon: Inbox, to: "/inbox", group: "Go to" },
  { id: "nav:contacts", title: "Contacts", icon: Users, to: "/contacts", group: "Go to" },
  { id: "nav:companies", title: "Companies", icon: Building2, to: "/companies", group: "Go to" },
  { id: "nav:deals", title: "Deals", icon: Briefcase, to: "/deals", group: "Go to" },
  { id: "nav:contracts", title: "Contracts", icon: FileSignature, to: "/contracts", group: "Go to" },
  { id: "nav:tasks", title: "Tasks", icon: CheckSquare, to: "/tasks", group: "Go to" },
  { id: "nav:sequences", title: "Sequences", icon: Activity, to: "/sequences", group: "Go to" },
  { id: "nav:campaigns", title: "Campaigns", icon: Activity, to: "/campaigns", group: "Go to" },
  { id: "nav:reports", title: "Reports", icon: BarChart3, to: "/reports", group: "Go to" },
  { id: "nav:forms", title: "Forms", icon: ClipboardList, to: "/forms", group: "Go to" },
  { id: "nav:admin", title: "Admin", icon: Settings, to: "/admin/users", group: "Go to" },
];

// Contract builder archived (client contracts live in Monjur). Hide its command
// unless CONTRACT_BUILDER_ENABLED is set. See docs/adr/ADR-archive-contract-builder.md.
const NAV_ITEMS = CONTRACT_BUILDER_ENABLED
  ? STATIC_NAV
  : STATIC_NAV.filter((i) => i.id !== "nav:contracts");

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CommandPalette({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounced server-side search
  const search = useQuery({
    queryKey: ["search", q],
    queryFn: () => searchApi.global(q),
    enabled: q.length >= 2,
    staleTime: 30_000,
  });

  const items: PaletteItem[] = useMemo(() => {
    const filtered = q
      ? NAV_ITEMS.filter((i) => i.title.toLowerCase().includes(q.toLowerCase()))
      : NAV_ITEMS;

    const dynamic: PaletteItem[] = [];
    if (search.data) {
      for (const c of search.data.contacts ?? []) {
        dynamic.push({
          id: `c:${c.id}`,
          title: c.full_name ?? c.email,
          hint: c.email,
          icon: Users,
          to: `/contacts/${c.id}`,
          group: "Contacts",
        });
      }
      for (const co of search.data.companies ?? []) {
        dynamic.push({
          id: `co:${co.id}`,
          title: co.name,
          hint: co.domain ?? undefined,
          icon: Building2,
          to: `/companies/${co.id}`,
          group: "Companies",
        });
      }
      for (const d of search.data.deals ?? []) {
        dynamic.push({
          id: `d:${d.id}`,
          title: d.name,
          hint: d.company_name ?? undefined,
          icon: Briefcase,
          to: `/deals?focus=${d.id}`,
          group: "Deals",
        });
      }
    }

    return [...filtered, ...dynamic];
  }, [q, search.data]);

  const grouped = useMemo(() => {
    const map = new Map<string, PaletteItem[]>();
    for (const item of items) {
      if (!map.has(item.group)) map.set(item.group, []);
      map.get(item.group)!.push(item);
    }
    return Array.from(map.entries());
  }, [items]);

  useEffect(() => {
    if (open) {
      setQ("");
      setHighlighted(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    setHighlighted(0);
  }, [q]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(items.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = items[highlighted];
      if (target) {
        navigate(target.to);
        onOpenChange(false);
      }
    }
  };

  let runningIndex = 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" hideClose className="overflow-hidden p-0">
        <div className="flex items-center gap-2 border-b border-line px-4 py-3">
          <Search className="h-4 w-4 text-ink-dim" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search contacts, companies, deals, jump to a page…"
            className="flex-1 bg-transparent text-sm placeholder:text-ink-dim focus:outline-none"
          />
          <kbd className="rounded border border-line bg-surface-panel2 px-1.5 py-0.5 text-[10px] font-mono text-ink-dim">esc</kbd>
        </div>

        <div className="max-h-96 overflow-y-auto p-2 scrollbar-thin">
          {grouped.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-ink-dim">No results.</p>
          ) : (
            grouped.map(([group, items]) => (
              <div key={group} className="mb-2">
                <p className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-ink-dim">
                  {group}
                </p>
                {items.map((item) => {
                  const myIndex = runningIndex++;
                  const Icon = item.icon ?? ArrowRight;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        navigate(item.to);
                        onOpenChange(false);
                      }}
                      onMouseEnter={() => setHighlighted(myIndex)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm",
                        highlighted === myIndex ? "bg-brand/10 text-brand-deep" : "text-ink hover:bg-surface-hover",
                      )}
                    >
                      <Icon className="h-3.5 w-3.5 opacity-70" />
                      <span className="flex-1 truncate">{item.title}</span>
                      {item.hint && <span className="truncate text-[11px] text-ink-dim">{item.hint}</span>}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-line bg-surface-panel2 px-4 py-2 text-[11px] text-ink-dim">
          <span><kbd className="font-mono">↑↓</kbd> navigate</span>
          <span><kbd className="font-mono">↵</kbd> select</span>
          <CommandPaletteShortcutHint />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CommandPaletteShortcutHint() {
  const mod = useModKey();
  return <span className="ml-auto">{mod}K to toggle</span>;
}
